import { useEffect, useRef } from 'react';
import { useAudioStore } from './useAudioStore';
import { getZenoMountId } from '@/utils/zenoMetadata';
import { isAzuraCastStream } from '@/utils/streamSource';
import type { TrackInfo } from '@/types';

const ZENO_API_BASE = 'https://api.zeno.fm/mounts/metadata/subscribe';

function parseStreamTitle(streamTitle: string): { artist: string; title: string } {
  const decoded = streamTitle
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&#39;/g, "'")
    .trim();

  if (decoded.includes(' - ')) {
    const [artist, ...titleParts] = decoded.split(' - ');
    return {
      artist: artist.trim(),
      title: titleParts.join(' - ').trim(),
    };
  }
  return { artist: '', title: decoded };
}

/** Pretplata na Zeno.fm Metadata API za trenutnu pjesmu */
export function useZenoMetadata() {
  const { currentStation, setCurrentTrack } = useAudioStore();
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!currentStation?.streamUrl) {
      setCurrentTrack(null);
      return;
    }
    if (isAzuraCastStream(currentStation.streamUrl)) {
      setCurrentTrack(null);
      return;
    }

    const mountId = getZenoMountId(currentStation.streamUrl);
    if (!mountId) {
      setCurrentTrack(null);
      return;
    }

    const url = `${ZENO_API_BASE}/${mountId}`;
    const es = new EventSource(url);

    es.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);
        const streamTitle = data?.streamTitle;
        if (typeof streamTitle === 'string' && streamTitle) {
          const { artist, title } = parseStreamTitle(streamTitle);
          const track: TrackInfo = {
            title: title || 'Nepoznata pjesma',
            artist: artist || 'Nepoznati izvođač',
          };
          setCurrentTrack(track);
        }
      } catch {
        // Ignoriraj nevaljane poruke
      }
    });

    es.onerror = () => {
      es.close();
    };

    eventSourceRef.current = es;

    return () => {
      es.close();
      eventSourceRef.current = null;
      setCurrentTrack(null);
    };
  }, [currentStation?.id, currentStation?.streamUrl, setCurrentTrack]);
}
